import { useState, useEffect } from 'react'
import { Calendar, Clock, User, ImagePlus, ChevronLeft, ChevronRight } from 'lucide-react'
import type { Booking } from '@/types/booking'
import { Badge } from '@/components/ui/badge'
import { fetchUserById, type ChatUserInfo } from '@/lib/chatData'
import { fetchChatById } from '@/lib/chatData'

interface UpcomingBookingProps {
  booking: Booking | null
}

export function UpcomingBooking({ booking }: UpcomingBookingProps) {
  const [isMobileDescriptionCollapsed, setIsMobileDescriptionCollapsed] = useState(true)
  const [userInfo, setUserInfo] = useState<ChatUserInfo | null>(null)
  const [chatImages, setChatImages] = useState<string[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)

  // Fetch user info
  useEffect(() => {
    if (booking?.userId) {
      fetchUserById(booking.userId)
        .then(info => setUserInfo(info))
        .catch(err => console.error('Error fetching user info:', err))
    }
  }, [booking?.userId])

  // Fetch chat and extract images
  useEffect(() => {
    if (booking?.chatId) {
      fetchChatById(booking.chatId)
        .then(chat => {
          if (chat && chat.messages) {
            const images = extractImagesFromMessages(chat.messages)
            setChatImages(images)
            setCurrentImageIndex(0) // Reset to first image when booking changes
          }
        })
        .catch(err => console.error('Error fetching chat:', err))
    }
  }, [booking?.chatId])

  // Helper function to extract all images from chat messages
  const extractImagesFromMessages = (messages: any[]): string[] => {
    const images: string[] = []
    
    messages.forEach(message => {
      if (message.type === 'image') {
        // Handle single image
        if (message.imageUrl) {
          images.push(message.imageUrl)
        }
        // Handle multiple images
        if (message.imageUrls && Array.isArray(message.imageUrls)) {
          images.push(...message.imageUrls)
        }
      }
    })
    
    return images
  }

  const handleToggleDescription = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsMobileDescriptionCollapsed(!isMobileDescriptionCollapsed)
  }

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentImageIndex(prev => 
      prev === 0 ? chatImages.length - 1 : prev - 1
    )
  }

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentImageIndex(prev => 
      prev === chatImages.length - 1 ? 0 : prev + 1
    )
  }

  if (!booking) {
    return (
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-3">Upcoming Booking</h2>
        <div className="bg-muted/30 rounded-lg p-6 text-center">
          <p className="text-muted-foreground">No upcoming bookings</p>
        </div>
      </div>
    )
  }

  const formatDate = (date: Date) => {
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' })
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear().toString().slice(-2)
    return `${weekday}, ${day}.${month}.${year}`
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const currentImage = chatImages.length > 0 ? chatImages[currentImageIndex] : null

  return (
    <div className="">
      <div className="bg-card border border-border rounded-lg overflow-hidden flex flex-col md:flex-row md:items-stretch" onClick={handleToggleDescription}>
        {/* Mobile: Top row with username, badge, and image */}
        <div className="md:hidden flex flex-row items-stretch justify-between border-b border-border">
          <div className="flex flex-col p-3 flex-1">
            {/* Small Upcoming Booking title */}
            <div className="text-xs font-semibold text-muted-foreground mb-2 tracking-wide">Upcoming Booking:</div>
            {/* Client info and type badge on same row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {userInfo?.avatarUrl ? (
                  <img
                    src={userInfo.avatarUrl}
                    alt={booking.clientUsername}
                    className="w-6 h-6 rounded-md object-cover"
                  />
                ) : (
                  <User className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="font-medium text-foreground">{booking.clientUsername}</span>
              </div>
              <Badge variant={booking.type === 'flash' ? 'default' : 'secondary'} className="w-fit">
                {booking.type === 'flash' ? 'Flash' : 'Custom'}
              </Badge>
            </div>

            {/* Date and time */}
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(booking.scheduledTimestamp)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{formatTime(booking.scheduledTimestamp)}</span>
              </div>
            </div>
          </div>

          {/* Image on the right for mobile top section */}
          <div className="w-[80px] flex-shrink-0 p-2 relative">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsImageModalOpen(true)
              }}
              className="relative w-full aspect-square overflow-hidden bg-muted rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
            >
              {currentImage ? (
                <img
                  src={currentImage}
                  alt="Chat design"
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImagePlus className="w-6 h-6 text-muted-foreground" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile: Bottom section with description and custom design status - Collapsible */}
        <div
          className={`md:hidden flex-1 overflow-hidden transition-all duration-500 ease-in-out ${
            isMobileDescriptionCollapsed ? 'max-h-0' : 'max-h-96'
          }`}
        >
          <div className="p-3 flex flex-col gap-2">
            {/* Summary */}
            <p className="text-sm text-muted-foreground">{booking.shortSummary}</p>
          </div>
        </div>

        {/* Desktop layout */}
        <div className="hidden md:flex flex-1 p-5 flex-col">
          {/* Small Upcoming Booking title */}
          <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Upcoming Booking</div>
          {/* Client info and type badge on same row */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {userInfo?.avatarUrl ? (
                <img
                  src={userInfo.avatarUrl}
                  alt={booking.clientUsername}
                  className="w-8 h-8 rounded-md object-cover"
                />
              ) : (
                <User className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="font-medium text-foreground text-sm">{booking.clientUsername}</span>
            </div>
            <Badge variant={booking.type === 'flash' ? 'default' : 'secondary'} className="w-fit">
              {booking.type === 'flash' ? 'Flash' : 'Custom'}
            </Badge>
          </div>

          {/* Summary */}
          <p className="text-sm text-muted-foreground mb-3">{booking.shortSummary}</p>

          {/* Date and time */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(booking.scheduledTimestamp)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{formatTime(booking.scheduledTimestamp)}</span>
            </div>
          </div>
        </div>

        {/* Desktop: Right side - Image */}
        <div className="hidden h-full md:flex w-[40%] max-w-[150px] items-center justify-center p-4 relative">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsImageModalOpen(true)
            }}
            className="relative w-full aspect-square overflow-hidden bg-muted rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
          >
            {currentImage ? (
              <img
                src={currentImage}
                alt="Chat design"
                className="w-full h-full object-cover"
              />
            ) : (
              <ImagePlus className="w-6 h-6 text-muted-foreground" />
            )}
          </button>
        </div>
      </div>

      {/* Image Modal */}
      {isImageModalOpen && currentImage && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div
            className="relative max-w-2xl max-h-[80vh] bg-background rounded-lg overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setIsImageModalOpen(false)}
              className="absolute top-3 right-3 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Image container */}
            <div className="flex-1 flex items-center justify-center overflow-auto">
              <img
                src={currentImage}
                alt="Design preview"
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {/* Navigation controls */}
            {chatImages.length > 1 && (
              <div className="flex items-center justify-between gap-4 p-4 bg-muted/50 border-t border-border">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handlePrevImage(e)
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="text-sm font-medium">Previous</span>
                </button>

                <span className="text-sm text-muted-foreground">
                  {currentImageIndex + 1} / {chatImages.length}
                </span>

                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleNextImage(e)
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
                >
                  <span className="text-sm font-medium">Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

